import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import useApi from "../hooks/useApi";
import Insignia5RC from "./Insignia5RC";

// Sección del perfil del difusor para activar los envíos de 5 RC.
//
// Un envío de 5 RC paga 7 USD, así que solo lo recibe un difusor cuyo canal
// revisó el equipo: registra dónde publica y el admin lo aprueba (o le dice
// qué falta). Las reglas viven en Backend/src/utils/canales5RC.js.

const AYUDA = {
  Editorial:
    "el sitio web donde publicas tus notas (no tus redes sociales)",
  Mención: null,
};

export default function Activacion5RC({ showSnack, onCambio }) {
  const { loadApi, loadingApi } = useApi();
  const [info, setInfo] = useState(null);
  const [canales, setCanales] = useState([""]);
  const [editando, setEditando] = useState(false);

  const cargar = () =>
    loadApi("productor/activacion-5rc", true, "get")
      .then((res) => {
        setInfo(res.data);
        setCanales(res.data.canales.length ? res.data.canales.map((c) => c.url) : [""]);
        setEditando(["sin_solicitud", "rechazada", "desactivada"].includes(res.data.estado));
        onCambio?.(res.data);
      })
      .catch(() => setInfo(null));

  useEffect(() => {
    cargar();
  }, []);

  if (!info) return null;

  const permitidas = info.permitidas.map((p) => p.nombre).join(", ");
  const queRegistrar =
    AYUDA[info.plan] || `tu canal donde publicas: ${permitidas}`;

  const enviar = () =>
    loadApi("productor/activacion-5rc", true, "put", {
      canales: canales.map((c) => c.trim()).filter(Boolean),
    })
      .then((res) => {
        showSnack?.(res.data.message);
        cargar();
      })
      .catch((e) => showSnack?.(e.message, "error"));

  return (
    <Stack
      bgcolor="#ffffff"
      p={{ xs: 2, sm: 3 }}
      gap={1.5}
      sx={{ borderRadius: "20px", boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px" }}
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="h6" fontWeight={800}>
          Envíos de 5 RC ({info.plan})
        </Typography>
        {info.estado === "aprobada" && <Insignia5RC tipo={info.tipo} />}
      </Stack>

      <Typography color="text.secondary" variant="body2">
        Un envío de 5 RC te paga 7 USD (uno Social, 1 USD). Para recibirlos,
        registra {queRegistrar}. El equipo revisa que exista y sea tuyo, y al
        aprobarlo aparece la insignia en tu perfil. Al aprobar un envío de 5
        RC, el link de la publicación tiene que ser de uno de estos canales.
      </Typography>

      {info.estado === "sin_solicitud" && (
        <Alert severity="info">
          Todavía no lo solicitaste: por ahora los artistas solo te pueden
          mandar Envíos Sociales (1 RC).
        </Alert>
      )}
      {info.estado === "pendiente" && (
        <Alert severity="info">
          El equipo está revisando tus canales. Te avisaremos por notificación
          y correo.
        </Alert>
      )}
      {info.estado === "aprobada" && (
        <Alert severity="success">
          ¡Activo! Los artistas ya te pueden mandar envíos de 5 RC.
        </Alert>
      )}
      {["rechazada", "desactivada"].includes(info.estado) && (
        <Alert severity="warning">
          <strong>
            {info.estado === "rechazada"
              ? "El equipo no aprobó tu solicitud."
              : "El equipo desactivó tus envíos de 5 RC."}
          </strong>
          {info.feedback && (
            <Box sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>{info.feedback}</Box>
          )}
          <Box mt={0.5}>Corrige tus canales y vuelve a solicitarlo.</Box>
        </Alert>
      )}

      {!editando ? (
        <Stack gap={0.5}>
          {info.canales.map((c) => (
            <Typography key={c.url} variant="body2" sx={{ overflowWrap: "anywhere" }}>
              <a href={c.url} target="_blank" rel="noopener noreferrer">
                {c.url}
              </a>
            </Typography>
          ))}
          <Button
            size="small"
            sx={{ alignSelf: "flex-start", mt: 1 }}
            onClick={() => setEditando(true)}
          >
            Cambiar canales
          </Button>
        </Stack>
      ) : (
        <Stack gap={1}>
          {info.estado === "aprobada" && (
            <Alert severity="warning">
              Si cambias tus canales, tus envíos de 5 RC quedan en pausa hasta
              que el equipo los revise de nuevo.
            </Alert>
          )}
          {canales.map((canal, i) => (
            <Stack key={i} direction="row" gap={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                label={i === 0 ? `Canal (${permitidas})` : `Canal ${i + 1}`}
                placeholder={info.plan === "Editorial" ? "https://tusitio.com" : "https://…"}
                value={canal}
                inputProps={{ "aria-label": `Canal ${i + 1}` }}
                onChange={(e) =>
                  setCanales((prev) => prev.map((c, j) => (j === i ? e.target.value : c)))
                }
              />
              {canales.length > 1 && (
                <IconButton
                  size="small"
                  aria-label={`Quitar canal ${i + 1}`}
                  onClick={() => setCanales((prev) => prev.filter((_, j) => j !== i))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
          <Stack direction="row" gap={1} flexWrap="wrap">
            {info.permitidas.length > 1 && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={canales.length >= info.maxCanales}
                onClick={() => setCanales((prev) => [...prev, ""])}
              >
                Agregar otro canal
              </Button>
            )}
            <LoadingButton
              variant="contained"
              size="small"
              loading={loadingApi.includes("put__productor/activacion-5rc")}
              disabled={!canales.some((c) => c.trim())}
              onClick={enviar}
            >
              {info.estado === "sin_solicitud" ? "Solicitar activación" : "Enviar a revisión"}
            </LoadingButton>
            {info.estado === "aprobada" || info.estado === "pendiente" ? (
              <Button size="small" onClick={cargar}>
                Cancelar
              </Button>
            ) : null}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
